import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CampaignCard from './components/CampaignCard';
import CampaignWizard from './components/CampaignWizard';
import WorkflowBuilder from './components/WorkflowBuilder';
import CampaignTemplates from './components/CampaignTemplates';
import CampaignAnalytics from './components/CampaignAnalytics';

const CampaignAutomation = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isWorkflowOpen, setIsWorkflowOpen] = useState(false);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const campaigns = [
    {
      id: 1,
      name: "Birthday Celebration Campaign",
      description: "Automated birthday wishes with personalized messages and follow-up sequences",
      status: "active",
      enrolledClients: 234,
      createdDate: "Oct 15, 2024",
      trigger: "Birthday",
      messageCount: 3,
      lastSent: "2 hours ago",
      metrics: {
        deliveryRate: 98.5,
        responseRate: 23.4,
        conversions: 12,
        revenue: "2,450"
      }
    },
    {
      id: 2,
      name: "Home Anniversary Sequence",
      description: "Celebrate client home purchase anniversaries with market updates",
      status: "active",
      enrolledClients: 189,
      createdDate: "Sep 28, 2024",
      trigger: "Home Anniversary",
      messageCount: 4,
      lastSent: "1 day ago",
      metrics: {
        deliveryRate: 97.2,
        responseRate: 18.7,
        conversions: 8,
        revenue: "1,890"
      }
    },
    {
      id: 3,
      name: "Post-Closing Follow-up",
      description: "Comprehensive 90-day follow-up sequence for new homeowners",
      status: "active",
      enrolledClients: 156,
      createdDate: "Oct 1, 2024",
      trigger: "Closing Date",
      messageCount: 5,
      lastSent: "3 hours ago",
      metrics: {
        deliveryRate: 99.1,
        responseRate: 31.2,
        conversions: 15,
        revenue: "3,200"
      }
    },
    {
      id: 4,
      name: "Seasonal Market Updates",
      description: "Quarterly market insights and property value updates",
      status: "paused",
      enrolledClients: 312,
      createdDate: "Aug 12, 2024",
      trigger: "Quarterly",
      messageCount: 4,
      lastSent: "1 week ago",
      metrics: {
        deliveryRate: 96.8,
        responseRate: 12.3,
        conversions: 5,
        revenue: "980"
      }
    },
    {
      id: 5,
      name: "Referral Request Campaign",
      description: "Gentle referral requests for satisfied clients with incentives",
      status: "draft",
      enrolledClients: 0,
      createdDate: "Oct 20, 2024",
      trigger: "6 months post-closing",
      messageCount: 3,
      lastSent: "Never",
      metrics: {
        deliveryRate: 0,
        responseRate: 0,
        conversions: 0,
        revenue: "0"
      }
    }
  ];

  const tabs = [
    { id: 'active', label: 'Active Campaigns', count: campaigns?.filter(c => c?.status === 'active')?.length },
    { id: 'paused', label: 'Paused', count: campaigns?.filter(c => c?.status === 'paused')?.length },
    { id: 'draft', label: 'Drafts', count: campaigns?.filter(c => c?.status === 'draft')?.length },
    { id: 'all', label: 'All Campaigns', count: campaigns?.length }
  ];

  const filteredCampaigns = campaigns?.filter(campaign => {
    const matchesTab = activeTab === 'all' || campaign?.status === activeTab;
    const matchesSearch = campaign?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         campaign?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const totalMetrics = {
    totalCampaigns: campaigns?.length,
    activeCampaigns: campaigns?.filter(c => c?.status === 'active')?.length,
    totalClients: campaigns?.reduce((sum, c) => sum + c?.enrolledClients, 0),
    avgResponseRate: (campaigns?.reduce((sum, c) => sum + c?.metrics?.responseRate, 0) / campaigns?.length)?.toFixed(1)
  };

  const handlePauseCampaign = (campaignId) => {
    console.log('Pausing campaign:', campaignId);
  };

  const handleEditCampaign = (campaignId) => {
    const campaign = campaigns?.find(c => c?.id === campaignId);
    setSelectedCampaign(campaign);
    setIsWorkflowOpen(true);
  };

  const handleViewAnalytics = (campaignId) => {
    const campaign = campaigns?.find(c => c?.id === campaignId);
    setSelectedCampaign(campaign);
    setIsAnalyticsOpen(true);
  };

  const handleSaveCampaign = (campaignData) => {
    console.log('Saving campaign:', campaignData);
  };

  const handleSaveWorkflow = (workflowData) => {
    console.log('Saving workflow:', workflowData);
    setIsWorkflowOpen(false);
  };

  const handleSelectTemplate = (template) => {
    console.log('Selected template:', template);
    setIsWizardOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16 pb-20 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Coming Soon Notice */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <Icon name="AlertCircle" size={20} className="text-warning" />
              <div>
                <p className="font-medium text-foreground">Campaign Automation - Coming Soon</p>
                <p className="text-sm text-muted-foreground">
                  This feature is currently under development. The interface below shows preview data only.
                </p>
              </div>
            </div>
          </div>

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Campaign Automation</h1>
              <p className="text-muted-foreground mt-2">
                Create and manage automated messaging sequences that nurture client relationships
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                onClick={() => setIsTemplatesOpen(true)}
                disabled
              >
                <Icon name="FileText" size={16} />
                Templates
              </Button>
              <Button onClick={() => setIsWizardOpen(true)} disabled>
                <Icon name="Plus" size={16} />
                New Campaign
              </Button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Campaigns</p>
                  <p className="text-2xl font-bold text-foreground">{totalMetrics?.totalCampaigns}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={24} className="text-primary" />
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Campaigns</p>
                  <p className="text-2xl font-bold text-foreground">{totalMetrics?.activeCampaigns}</p>
                </div>
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Play" size={24} className="text-success" />
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Enrolled Clients</p>
                  <p className="text-2xl font-bold text-foreground">{totalMetrics?.totalClients?.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-accent" />
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Response Rate</p>
                  <p className="text-2xl font-bold text-foreground">{totalMetrics?.avgResponseRate}%</p>
                </div>
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-warning" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div className="flex items-center space-x-1 mb-4 sm:mb-0">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {tab?.label}
                  <span className="ml-2 px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs">
                    {tab?.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent w-full sm:w-80"
              />
            </div>
          </div>

          {/* Campaigns Grid */}
          <div className="space-y-6">
            {filteredCampaigns?.length > 0 ? (
              filteredCampaigns?.map((campaign) => (
                <CampaignCard
                  key={campaign?.id}
                  campaign={campaign}
                  onPause={handlePauseCampaign}
                  onEdit={handleEditCampaign}
                  onViewAnalytics={handleViewAnalytics}
                />
              ))
            ) : (
              <div className="text-center py-12">
                <Icon name="Zap" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No campaigns found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery ? 'Try adjusting your search terms' : 'Get started by creating your first automated campaign'}
                </p>
                <div className="flex items-center justify-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsTemplatesOpen(true)}
                  >
                    <Icon name="FileText" size={16} />
                    Browse Templates
                  </Button>
                  <Button onClick={() => setIsWizardOpen(true)}>
                    <Icon name="Plus" size={16} />
                    Create Campaign
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modals */}
      <CampaignWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onSave={handleSaveCampaign}
      />
      <WorkflowBuilder
        campaign={selectedCampaign}
        isOpen={isWorkflowOpen}
        onClose={() => setIsWorkflowOpen(false)}
        onSave={handleSaveWorkflow}
      />
      <CampaignTemplates
        isOpen={isTemplatesOpen}
        onClose={() => setIsTemplatesOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />
      <CampaignAnalytics
        campaign={selectedCampaign}
        isOpen={isAnalyticsOpen}
        onClose={() => setIsAnalyticsOpen(false)}
      />
    </div>
  );
};

export default CampaignAutomation;